import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { t, tLocation, tBusinessUnit } from '../i18n';

function ChangePasswordTab({ user }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user?.provider) {
    const providerName = user.provider.charAt(0).toUpperCase() + user.provider.slice(1);
    return (
      <div className="fade-up" style={{ color: 'var(--text-muted)', fontSize: 15, padding: '8px 0' }}>
        {t('profileOAuthPassword').replace('{provider}', providerName)}
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) { setError(t('profilePasswordTooShort')); return; }
    if (newPassword !== confirmPassword) { setError(t('profilePasswordMismatch')); return; }
    setLoading(true);
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      setSuccess(true);
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: 8,
    border: '1.5px solid var(--border)', fontFamily: 'DM Sans',
    fontSize: 15, boxSizing: 'border-box', marginTop: 6
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.05em' };

  return (
    <div className="fade-up">
      <h3 style={{ marginBottom: 20 }}>{t('profileChangePassword')}</h3>
      <form onSubmit={handleSubmit} style={{ maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={labelStyle}>{t('profileCurrentPassword')}</label>
          <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} style={inputStyle} required />
        </div>
        <div>
          <label style={labelStyle}>{t('profileNewPassword')}</label>
          <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={inputStyle} required />
        </div>
        <div>
          <label style={labelStyle}>{t('profileConfirmPassword')}</label>
          <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} style={inputStyle} required />
        </div>
        {error && <p style={{ color: '#ef4444', fontSize: 14, margin: 0 }}>{error}</p>}
        <div>
          <button type="submit" className="btn btn-primary" disabled={loading} style={{ minWidth: 160 }}>
            {success ? t('profilePasswordUpdated') : loading ? '...' : t('profileUpdatePassword')}
          </button>
        </div>
      </form>
    </div>
  );
}

function DeleteAccountSection({ user }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const needsPassword = !user?.provider;

  const handleDelete = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.delete('/profile/me', { data: needsPassword ? { password } : {} });
      logout();
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
      setLoading(false);
    }
  };

  return (
    <div className="fade-up" style={{ marginTop: 40, border: '1.5px solid #fecaca', borderRadius: 12, padding: 20 }}>
      <h3 style={{ marginBottom: 6, color: '#dc2626' }}>{t('profileDangerZoneTitle')}</h3>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>{t('profileDeleteAccountDesc')}</p>
      {!confirming ? (
        <button type="button" className="btn" onClick={() => setConfirming(true)}
          style={{ background: '#fef2f2', border: '1.5px solid #dc2626', color: '#dc2626', fontWeight: 600 }}>
          {t('profileDeleteAccountBtn')}
        </button>
      ) : (
        <form onSubmit={handleDelete} style={{ maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#dc2626', margin: 0 }}>{t('profileDeleteConfirmDesc')}</p>
          {needsPassword && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.05em' }}>
                {t('profileDeleteConfirmPasswordLabel')}
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="input"
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 6 }}
                required
                autoFocus
              />
            </div>
          )}
          {error && <p style={{ color: '#ef4444', fontSize: 14, margin: 0 }}>{error}</p>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" className="btn" disabled={loading}
              style={{ background: '#dc2626', border: 'none', color: 'white', fontWeight: 600 }}>
              {loading ? '…' : t('profileDeleteConfirmBtn')}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => { setConfirming(false); setPassword(''); setError(''); }}>
              {t('profileDeleteCancelBtn')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

const GROUP_OPTIONS = [
  { size: 2, label: t('groupSize2Label'), desc: t('groupSize2Desc'), heads: 2 },
  { size: 3, label: t('groupSize3Label'), desc: t('groupSize3Desc'), heads: 3 },
  { size: 4, label: t('groupSize4Label'), desc: t('groupSize4Desc'), heads: 4 }
];

function HeadIcons({ count, active }) {
  const color = active ? 'var(--match-a)' : '#9ca3af';
  return (
    <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
      {Array.from({ length: count }).map((_, i) => (
        <svg key={i} width="22" height="22" viewBox="0 0 24 24" fill={color}>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
        </svg>
      ))}
    </div>
  );
}

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const TABS = [
    t('profileTabBusiness'),
    t('profileTabLocation'),
    t('profileTabLanguages'),
    t('profileTabPreferences'),
    t('profileTabPassword')
  ];
  const [tab, setTab] = useState(TABS[0]);
  const [allBusinessUnits, setAllBusinessUnits] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [allLanguages, setAllLanguages] = useState([]);
  const [myBusinessUnits, setMyBusinessUnits] = useState([]);
  const [interestedBusinessUnits, setInterestedBusinessUnits] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [groupSizePreferences, setGroupSizePreferences] = useState([2]);
  const [weeklyLunchLimit, setWeeklyLunchLimit] = useState(1);
  const [saved, setSaved] = useState(false);

  // Name editing
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [nameSaving, setNameSaving] = useState(false);

  useEffect(() => {
    api.get('/profile/business-units/all').then(r => setAllBusinessUnits(r.data));
    api.get('/profile/locations/all').then(r => setAllLocations(r.data));
    api.get('/profile/languages/all').then(r => setAllLanguages(r.data));
    if (user) {
      setMyBusinessUnits(user.my_business_units?.map(bu => bu.id) || []);
      setInterestedBusinessUnits(user.interested_business_units?.map(bu => bu.id) || []);
      setSelectedLocations(user.locations?.map(l => l.id) || []);
      setSelectedLanguages(user.languages?.map(l => l.id) || []);
      setGroupSizePreferences(user.group_size_preferences || [2]);
      setWeeklyLunchLimit(user.max_lunches_per_week || 1);
      setNameValue(user.name || '');
    }
  }, [user]);

  const saveName = async () => {
    if (!nameValue.trim() || nameValue.trim() === user?.name) { setEditingName(false); return; }
    setNameSaving(true);
    try {
      await api.put('/profile/name', { name: nameValue.trim() });
      await refreshUser();
      setEditingName(false);
    } catch {
      // keep editing open on error
    } finally {
      setNameSaving(false);
    }
  };

  const save = async () => {
    await api.put('/profile/group-size-preference', { groupSizes: groupSizePreferences });
    await api.put('/profile/weekly-lunch-limit', { maxLunchesPerWeek: weeklyLunchLimit });
    await api.put('/profile/my-business-unit', { businessUnitIds: myBusinessUnits });
    await api.put('/profile/interested-business-units', { businessUnitIds: interestedBusinessUnits });
    await api.put('/profile/locations', { locationIds: selectedLocations });
    await api.put('/profile/languages', { languageIds: selectedLanguages });
    await refreshUser();
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const goNext = () => {
    const idx = TABS.indexOf(tab);
    setTab(TABS[(idx + 1) % TABS.length]);
  };

  const goPrev = () => {
    const idx = TABS.indexOf(tab);
    setTab(TABS[(idx - 1 + TABS.length) % TABS.length]);
  };

  const saveAndNext = async () => {
    await save();
    goNext();
  };

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container">
        {/* Profile header */}
        <div className="card" style={{ padding: 28, display: 'flex', gap: 20, alignItems: 'center', marginBottom: 28 }}>
          <div className="avatar" style={{ background: user?.avatar_color, width: 60, height: 60, fontSize: 24 }}>
            {(nameValue || user?.name)?.[0]}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {editingName ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  className="input"
                  value={nameValue}
                  onChange={e => setNameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }}
                  autoFocus
                  style={{ fontSize: 18, fontWeight: 600, maxWidth: 280 }}
                />
                <button className="btn btn-primary" onClick={saveName} disabled={nameSaving} style={{ padding: '6px 16px' }}>
                  {nameSaving ? '…' : 'Save'}
                </button>
                <button className="btn btn-ghost" onClick={() => { setNameValue(user?.name || ''); setEditingName(false); }} style={{ padding: '6px 12px' }}>
                  Cancel
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2 style={{ fontSize: 22, marginBottom: 2 }}>{user?.name}</h2>
                <button
                  onClick={() => { setNameValue(user?.name || ''); setEditingName(true); }}
                  title="Edit name"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, lineHeight: 1 }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
              </div>
            )}
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>{user?.email}</p>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'var(--green-50)', padding: 4, borderRadius: 10, width: 'fit-content', flexWrap: 'wrap' }}>
          {TABS.map(tabName => (
            <button
              key={tabName}
              onClick={() => setTab(tabName)}
              style={{
                padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
                fontFamily: 'DM Sans', fontSize: 14, fontWeight: tab === tabName ? 600 : 400,
                background: tab === tabName ? 'white' : 'transparent',
                color: tab === tabName ? 'var(--match-a)' : 'var(--text-muted)',
                boxShadow: tab === tabName ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.15s', textTransform: 'capitalize', flexShrink: 0
              }}
            >
              {tabName}
            </button>
          ))}
        </div>

        <div className="card profile-card" style={{ padding: 32 }}>
          {tab === t('profileTabBusiness') && (
            <div className="fade-up">
              <div style={{ marginBottom: 32 }}>
                <h3 style={{ marginBottom: 6 }}>{t('profileMyUnits')}</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>{t('profileSelectApplies')}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {allBusinessUnits.map(bu => (
                    <div key={bu.id} className={`chip ${myBusinessUnits.includes(bu.id) ? 'selected' : ''}`}
                      onClick={() => setMyBusinessUnits(prev => prev.includes(bu.id) ? prev.filter(x => x !== bu.id) : [...prev, bu.id])}>
                      {tBusinessUnit(bu.name)}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 style={{ marginBottom: 6 }}>{t('profileInterestedUnits')}</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>{t('profileMatchLocation')}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <div className={`chip ${interestedBusinessUnits.length === allBusinessUnits.length ? 'selected' : ''}`}
                    onClick={() => setInterestedBusinessUnits(interestedBusinessUnits.length === allBusinessUnits.length ? [] : allBusinessUnits.map(bu => bu.id))}>
                    {t('anyLabel')}
                  </div>
                  {allBusinessUnits.map(bu => (
                    <div key={bu.id} className={`chip ${interestedBusinessUnits.includes(bu.id) ? 'selected' : ''}`}
                      onClick={() => setInterestedBusinessUnits(prev => prev.includes(bu.id) ? prev.filter(x => x !== bu.id) : [...prev, bu.id])}>
                      {tBusinessUnit(bu.name)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === t('profileTabLocation') && (
            <div className="fade-up">
              <h3 style={{ marginBottom: 6 }}>{t('profileTabLocation')}</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>{t('profileMatchLocation')}</p>
              {Object.entries(allLocations.reduce((g, l) => { (g[l.country] = g[l.country] || []).push(l); return g; }, {})).map(([region, locations]) => (
                <div key={region} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{tLocation(region)}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {locations.map(l => (
                      <div key={l.id} className={`chip ${selectedLocations.includes(l.id) ? 'selected' : ''}`}
                        onClick={() => setSelectedLocations(prev => prev.includes(l.id) ? prev.filter(x => x !== l.id) : [...prev, l.id])}>
                        {tLocation(l.name)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === t('profileTabLanguages') && (
            <div className="fade-up">
              <h3 style={{ marginBottom: 20 }}>{t('profileLanguagesSpeak')}</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {allLanguages.map(l => (
                  <div key={l.id} className={`chip ${selectedLanguages.includes(l.id) ? 'selected' : ''}`}
                    onClick={() => setSelectedLanguages(prev => prev.includes(l.id) ? prev.filter(x => x !== l.id) : [...prev, l.id])}>
                    {l.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === t('profileTabPreferences') && (
            <div className="fade-up">
              <h3 style={{ marginBottom: 6 }}>{t('profileGroupSizeTitle')}</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>{t('profileMatchLocation')}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {GROUP_OPTIONS.map(opt => {
                  const active = groupSizePreferences.includes(opt.size);
                  return (
                    <div key={opt.size}
                      onClick={() => setGroupSizePreferences(prev =>
                        prev.includes(opt.size) ? (prev.length > 1 ? prev.filter(s => s !== opt.size) : prev) : [...prev, opt.size]
                      )}
                      style={{
                        border: `2px solid ${active ? 'var(--match-a)' : 'var(--border)'}`,
                        borderRadius: 12, padding: '16px 20px', cursor: 'pointer',
                        background: active ? 'var(--green-50)' : 'white',
                        display: 'flex', alignItems: 'center', gap: 16, transition: 'all 0.15s'
                      }}>
                      <HeadIcons count={opt.heads} active={active} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 16, color: active ? 'var(--match-a)' : 'inherit' }}>{opt.label}</div>
                        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{opt.desc}</div>
                      </div>
                      {active && <div style={{ marginLeft: 'auto', color: 'var(--match-a)', fontWeight: 700, fontSize: 18 }}>✓</div>}
                    </div>
                  );
                })}
              </div>

              <div style={{ marginTop: 32 }}>
                <h3 style={{ marginBottom: 6 }}>Weekly lunch limit</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>
                  Maximum number of lunches you want to be matched for per week.
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      onClick={() => setWeeklyLunchLimit(n)}
                      style={{
                        width: 48, height: 48, borderRadius: 10, border: 'none', cursor: 'pointer',
                        fontFamily: 'DM Sans', fontSize: 18, fontWeight: 700,
                        background: weeklyLunchLimit === n ? 'var(--match-a)' : 'var(--green-50)',
                        color: weeklyLunchLimit === n ? 'white' : 'var(--text-muted)',
                        transition: 'all 0.15s'
                      }}
                    >{n}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === t('profileTabPassword') && (
            <>
              <ChangePasswordTab user={user} />
              <DeleteAccountSection user={user} />
            </>
          )}

          {/* Action buttons */}
          <div style={{ marginTop: 28, display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="btn" onClick={goPrev}
              style={{ minWidth: 100, background: 'var(--green-50)', border: '1.5px solid var(--match-a)', color: 'var(--match-a)', fontWeight: 600 }}>
              ← Previous
            </button>
            <div style={{ flex: 1 }} />
            {tab !== t('profileTabPassword') && (
              <button className="btn btn-primary" onClick={save} style={{ minWidth: 120 }}>
                {saved ? t('profileSaved') : t('profileSaveChanges')}
              </button>
            )}
            <button className="btn" onClick={tab !== t('profileTabPassword') ? saveAndNext : goNext}
              style={{ minWidth: 100, background: 'var(--green-50)', border: '1.5px solid var(--match-a)', color: 'var(--match-a)', fontWeight: 600 }}>
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
